import React from 'react';
import ArchitectureLayout from 'components/architecture/ArchitectureLayout';
import { useTranslation } from 'utils/i18n';
import translations from './translations';

/**
 * Architecture Component
 * Main architecture overview page explaining transparency/trust while maintaining security
 */
const Architecture = () => {
  const { language } = useTranslation();

  // Get page-specific translations
  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  const sections = [
    {
      id: 'introduction',
      title: t('sections.introduction.title', 'Our Commitment to Transparency'),
      content: t('sections.introduction.content', `At B-esper, we believe that trust is built through transparency. We are proud of our architecture and want to share it with you while maintaining the highest standards of security.

Our platform is built on enterprise-grade Azure infrastructure, designed for scalability, reliability, and security. We understand that when you trust us with your data and operations, you deserve to know how we protect and process it.`)
    },
    {
      id: 'architecture-overview',
      title: t('sections.architectureOverview.title', 'Architecture Overview'),
      content: t('sections.architectureOverview.content', `Our architecture is built on four core pillars:

**Runtime**: The execution enviroment that powers our serverless functions and microservices.

**Gateway**: The API gateway that manages routing, authentication, and rate limiting for all API requests.

**Compute**: The serverless compute infrastructure that scales automatically with demand.

**Database**: The globally distributed database that ensures data consistency and availability.

Each component is designed with security, scalability, and reliability in mind.`)
    },
    {
      id: 'security',
      title: t('sections.security.title', 'Security First'),
      content: t('sections.security.content', `While we are transparent about our architecture, we maintain strict security practices:

- All data is encrypted in transit and at rest
- Multi-tenant isolation ensures your data remains private
- Regular security audits and compliance certifications
- Role-based access control (RBAC) for all operations
- Secure secret management through Azure Key Vault

We believe transparency and security go hand in hand.`)
    },
    {
      id: 'explore',
      title: t('sections.explore.title', 'Explore Our Architecture'),
      content: t('sections.explore.content', `Use the navigation on the left to explore:

- **Runtime**: Learn about our serverless execution enviroment
- **Gateway**: Understand our API management and routing
- **Compute**: Discover our scalable compute infrastructure
- **Database**: Explore our data storage and retrieval systems
- **Architecture Components**: See all components at a glance
- **Processes**: Review our API endpoints and operations

Each section provides detailed technical information about how our platform works.`)
    }
  ];

  return (
    <ArchitectureLayout
      title={t('title', 'Architecture')}
      lastUpdated={t('lastUpdated', '2025-01-15')}
      sections={sections}
    />
  );
};

export default Architecture;




